/**
 * THE HELD COPY — a local copy that could not be updated is never treated as
 * current (owner report 2026-09-22 — Miles: his newest message in a group thread
 * "is appearing and disappearing like really bad … like whiplash to the user").
 *
 * When the browser's storage bin is full, localStorage refuses a thread's write
 * and its copy on disk stays FROZEN at the last list that fit. Everything that
 * later reads that key — the reader behind addMessage's base, the queued-turn
 * lane's routed updates, the reconcile poll, the persist guards' own baseline,
 * the skip-unchanged check — used to read that frozen list and act on it as if
 * it were current: a queued reply could not find its own placeholder, and the
 * persist path re-attempted the same refused write (and its 8x-payload reclaim,
 * and its broadcast) on every pass.
 *
 * This module keeps, per key, the JSON this tab last TRIED to store when
 * localStorage refused it. The chokepoints read through it
 * (`utils.getFromLocalStorage`, `utils.localStorageHasKey`,
 * `threadPersist.rehydrateThreadMessages`, the persist guards), so every
 * per-thread read in the app gets this tab's latest copy without being edited
 * one by one. A held copy is released the moment localStorage holds a fresher
 * copy of that key from ANY tab — this tab's own successful write, or a
 * `storage` event naming the key (another tab stored it) — so a held copy can
 * never outlive the frozen state it stands in for.
 *
 * Memory only, per tab, bounded by total characters (oldest-touched first out).
 * It is not a store: a reload starts empty and reads the frozen copy exactly as
 * before, while the server listener and the IndexedDB rehydrate heal it.
 *
 * Imports NOTHING on purpose: storageQuota, utils and threadPersist all call
 * it, and eleven chat-isolation suites mock `../threadPersist` wholesale — a
 * helper living there would be erased by the mock (the 68d73d5 trap: "the
 * constant lives where a mock cannot erase it").
 */

/**
 * The most characters held across all keys. A thread's cache can be large (the
 * one in the 2026-09-22 recording was ~430k characters for its newest 100 rows),
 * and the origin's whole localStorage is ~5M characters in Chromium, so this
 * holds a full bin's worth without ever becoming the memory problem.
 */
export const MAX_HELD_CHARS = 5 * 1024 * 1024;

/** Insertion order = recency (a touch re-inserts), so the first key is the coldest. */
const held = new Map<string, string>();
let heldChars = 0;
let storageListenerInstalled = false;

function dropKey(key: string): void {
  const prev = held.get(key);
  if (prev === undefined) return;
  heldChars -= prev.length;
  held.delete(key);
}

/**
 * Another tab stored (or removed) the key: localStorage now holds a copy fresher
 * than this tab's frozen one, so the held copy has nothing left to stand in for.
 * `key === null` is `localStorage.clear()` from another tab — every copy goes.
 */
function onStorageEvent(event: StorageEvent): void {
  try {
    if (typeof localStorage !== 'undefined' && event.storageArea && event.storageArea !== localStorage) return;
  } catch { /* storage unavailable — fall through and release */ }
  if (event.key === null) {
    held.clear();
    heldChars = 0;
    return;
  }
  dropKey(event.key);
}

function ensureStorageListener(): void {
  if (storageListenerInstalled) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  try {
    window.addEventListener('storage', onStorageEvent);
    storageListenerInstalled = true;
  } catch { /* no listener ⇒ a held copy is released only by this tab's own writes */ }
}

/** localStorage refused this write: hold what this tab tried to store. */
export function holdRefusedWrite(key: string, json: string): void {
  if (typeof key !== 'string' || !key || typeof json !== 'string') return;
  ensureStorageListener();
  dropKey(key);
  held.set(key, json);
  heldChars += json.length;
  // Coldest first; the key just written always survives, however large.
  for (const [coldKey] of held) {
    if (heldChars <= MAX_HELD_CHARS) break;
    if (coldKey === key) continue;
    dropKey(coldKey);
  }
}

/**
 * localStorage now holds a current copy of this key (a write landed), or the
 * key is gone (removed, evicted). A save held back for it goes too: a removed
 * key must never come back, and a landed write already carried it.
 */
export function releaseHeldCopy(key: string): void {
  dropDeferredThreadSave(key);
  if (held.size === 0) return;
  dropKey(key);
}

/** This tab's latest copy of the key when localStorage could not take it; else null. */
export function readHeldCopy(key: string): string | null {
  if (held.size === 0) return null;
  const value = held.get(key);
  return value === undefined ? null : value;
}

/** Release every held copy whose key the predicate names (purges, sign-out). */
export function releaseHeldCopiesWhere(predicate: (key: string) => boolean): void {
  // A purge also cancels every save held back for the keys it removes, so a
  // timer can never write a purged (signed-out) person's thread back.
  for (const key of Array.from(deferred.keys())) {
    let match = false;
    try { match = predicate(key); } catch { match = false; }
    if (match) dropDeferredThreadSave(key);
  }
  if (held.size === 0) return;
  for (const key of Array.from(held.keys())) {
    let match = false;
    try { match = predicate(key); } catch { match = false; }
    if (match) dropKey(key);
  }
}

/** Test seam: how many keys are held right now. */
export function heldCopyCount(): number {
  return held.size;
}

/** Test seam: forget everything (module state survives between tests otherwise). */
export function __resetHeldCopiesForTests(): void {
  held.clear();
  heldChars = 0;
  for (const key of Array.from(deferred.keys())) dropDeferredThreadSave(key);
}

// ─────────────────────────────────────────────────────────────────────────────
// THE HELD-BACK SAVE (2026-09-28, Malaika's report: the site "gets stuck"
// while she types). While a reply streams, its words change on every animation
// frame, and every change re-saved the WHOLE thread: serialize, compare, write,
// and a copy of the whole value pushed to every other prest0 tab (about 4 ms a
// frame for a 373-message thread on a fast machine, on the main thread).
//
// The store now hands a save that would only change the words of a row still
// in flight to deferThreadSave: it runs at most THREAD_SAVE_HOLD_MS later, and
// SOONER whenever anything in this tab touches that key. Every chokepoint that
// reads, writes or asks about a thread's key runs the held save first
// (utils.getFromLocalStorage / localStorageHasKey / setToLocalStorage,
// threadPersist's persist and rehydrate), so no code in this tab ever reads an
// older copy than it would have, and writes keep today's order. A key that is
// removed, evicted or purged cancels its held save (releaseHeldCopy,
// releaseHeldCopiesWhere). The page being hidden, closed or reloaded by the
// self-heal runs every held save at once.
//
// What changes, stated: another tab sees a streaming reply's words up to a
// second later, and a tab killed outright loses up to a second of words in its
// local copy (the reply itself is on the server, and a reload resumes it).
// ─────────────────────────────────────────────────────────────────────────────

/** The longest a save is held back. */
export const THREAD_SAVE_HOLD_MS = 1_000;

/** The self-heal's before-reload event (errorHandlers.HEAL_BEFORE_RELOAD_EVENT, pinned equal). */
export const SAVE_ALL_BEFORE_RELOAD_EVENT = 'prest0:before-self-heal-reload';

const deferred = new Map<string, () => void>();
const deferredTimers = new Map<string, ReturnType<typeof setTimeout>>();
let flushListenersInstalled = false;

function clearDeferredTimer(key: string): void {
  const timer = deferredTimers.get(key);
  if (timer !== undefined) {
    clearTimeout(timer);
    deferredTimers.delete(key);
  }
}

function ensureFlushListeners(): void {
  if (flushListenersInstalled) return;
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  try {
    window.addEventListener('pagehide', flushAllDeferredThreadSaves);
    window.addEventListener(SAVE_ALL_BEFORE_RELOAD_EVENT, flushAllDeferredThreadSaves);
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushAllDeferredThreadSaves();
      });
    }
    flushListenersInstalled = true;
  } catch { /* no listeners ⇒ held saves still run on their timer and on any touch */ }
}

/**
 * Hold this key's save back: `save` replaces any save already held for the key
 * (the newest list wins) and runs within `holdMs`, or sooner when anything
 * touches the key. The timer starts with the first save held, and a later one
 * does not push it back, so a long reply is still saved about once a second.
 */
export function deferThreadSave(key: string, save: () => void, holdMs: number = THREAD_SAVE_HOLD_MS): void {
  if (typeof key !== 'string' || !key || typeof save !== 'function') return;
  ensureFlushListeners();
  deferred.set(key, save);
  if (!deferredTimers.has(key)) {
    deferredTimers.set(key, setTimeout(() => {
      deferredTimers.delete(key);
      flushDeferredThreadSave(key);
    }, holdMs));
  }
}

/**
 * Run this key's held save now, once. It leaves the registry BEFORE it runs,
 * so the reads and writes the save itself makes do not run it again.
 */
export function flushDeferredThreadSave(key: string): void {
  if (deferred.size === 0) return;
  const save = deferred.get(key);
  if (save === undefined) return;
  deferred.delete(key);
  clearDeferredTimer(key);
  try { save(); } catch { /* a failed save is the persist path's to report */ }
}

/** Run every held save now (the page is going away, or being reloaded). */
export function flushAllDeferredThreadSaves(): void {
  for (const key of Array.from(deferred.keys())) flushDeferredThreadSave(key);
}

/** Cancel this key's held save (the key was removed, evicted or purged). */
export function dropDeferredThreadSave(key: string): void {
  if (!deferred.has(key)) return;
  deferred.delete(key);
  clearDeferredTimer(key);
}

/** Test seam: how many saves are held right now. */
export function deferredThreadSaveCount(): number {
  return deferred.size;
}
